import { AuthenticationResult, BrowserAuthError, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { CanceledError } from 'axios';
import { msalInstance, loginRequest } from './msalConfig';
import { User } from '../dataModels';

let signedOut = false;
let sessionVersion = 0;
let redirectPending: Promise<never> | undefined;

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    signedOut = false;
    sessionVersion += 1;
  }
});

function assertSessionActive(version: number) {
  if (signedOut || version !== sessionVersion) {
    throw new CanceledError('API request cancelled: the authentication session ended.');
  }
}

function renewInteractively(scope: string, loginHint: string, version: number): Promise<never> {
  assertSessionActive(version);
  if (!redirectPending) {
    redirectPending = msalInstance.acquireTokenRedirect({
      ...loginRequest,
      scopes: [scope],
      loginHint,
    }).then(() => {
      throw new CanceledError('API request cancelled: authentication is redirecting.');
    }).finally(() => {
      redirectPending = undefined;
    });
  }
  return redirectPending;
}

function getActiveAccount() {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

export const aquireToken = async (scope: string): Promise<{ accessToken: string }> => {
  const version = sessionVersion;
  assertSessionActive(version);
  const account = getActiveAccount();
  if (!account) {
    throw new CanceledError('API request cancelled: authentication is not complete.');
  }
  try {
    const result: AuthenticationResult = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account,
    });
    assertSessionActive(version);
    return { accessToken: result.accessToken };
  } catch (error) {
    assertSessionActive(version);
    if (
      error instanceof InteractionRequiredAuthError ||
      (error instanceof BrowserAuthError && error.errorCode === 'monitor_window_timeout')
    ) {
      return renewInteractively(scope, account.username, version);
    }
    throw error;
  }
};

export const getUser = (): User | null => {
  const account = getActiveAccount();
  if (!account) {
    return null;
  }
  const name = (account.idTokenClaims as Record<string, unknown> | undefined)?.name;
  return { name: typeof name === 'string' ? name : account.username, email: account.username };
};

export const signOut = async () => {
  signedOut = true;
  sessionVersion += 1;
  await msalInstance.logoutRedirect();
};
